import { env } from "../env";
import { buildKakaoAuthorizeUrl, buildKakaoSdkInitUrl } from "./kakaoOidc";
import {
  detectKakaoLoginRuntime,
  shouldUseKakaoSdkLogin,
  type KakaoLoginRuntime,
} from "./kakaoRuntime";
import { loadKakaoSdk, type KakaoSdk } from "./kakaoSdk";

export const KAKAO_LOGIN_SCOPE = "openid profile_nickname profile_image";

// CAUTION: protected auth flow. Keep the public web entry on the stable REST
// authorize route until the production JavaScript key is explicitly aligned
// with the server REST app and verified end-to-end.
const KAKAO_SDK_LOGIN_ENABLED = false;

type KakaoSdkInitPayload = {
  nonce: string;
  redirectUri: string;
  state: string;
};

type StartKakaoLoginParams = {
  next?: string | null;
  runtime?: KakaoLoginRuntime;
  fetchSdkInit?: (next?: string | null) => Promise<KakaoSdkInitPayload>;
  loadSdk?: () => Promise<KakaoSdk>;
  assignLocation?: (url: string) => void;
};

async function defaultFetchSdkInit(next?: string | null) {
  const response = await fetch(buildKakaoSdkInitUrl(next), {
    credentials: "include",
    method: "GET",
  });

  if (!response.ok) {
    throw new Error("Failed to prepare Kakao SDK login.");
  }

  return response.json() as Promise<KakaoSdkInitPayload>;
}

export async function startKakaoLogin({
  next,
  runtime = detectKakaoLoginRuntime(),
  fetchSdkInit = defaultFetchSdkInit,
  loadSdk = () => loadKakaoSdk({ key: env.kakaoJavascriptKey }),
  assignLocation = (url) => window.location.assign(url),
}: StartKakaoLoginParams = {}) {
  const fallbackUrl = buildKakaoAuthorizeUrl(next);

  if (!KAKAO_SDK_LOGIN_ENABLED || !shouldUseKakaoSdkLogin(runtime)) {
    assignLocation(fallbackUrl);
    return;
  }

  try {
    const [sdkInit, sdk] = await Promise.all([
      fetchSdkInit(next),
      loadSdk(),
    ]);

    sdk.Auth.authorize({
      nonce: sdkInit.nonce,
      redirectUri: sdkInit.redirectUri,
      scope: KAKAO_LOGIN_SCOPE,
      state: sdkInit.state,
      throughTalk: true,
    });
  } catch {
    // CAUTION: protected auth flow. If SDK prep fails, keep customer entry alive
    // by falling back to the stable REST authorize route.
    assignLocation(fallbackUrl);
  }
}
